import {name, Panes, prefixWithSeparator, RelationAutocomplete, Select} from '@management-ui/core';
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useFormContext} from 'react-hook-form';
import {ServiceContext} from '../../../../components/Services';
import {useOptions} from '../../../../hooks';
import withTemplatedFields from '../JobDetailsForm/withTemplatedFields';
import NewCompanyForm from '../NewCompanyForm';
import NewContactForm from '../NewContactForm';

const Fields = ({job = {}, prefix = '', fields, multiplePanes = true, children}) => {
  const services = useContext(ServiceContext);
  const {/** @var {function(string): string} */getValues, setValue, watch} = useFormContext();

  const companies = useRef([]);
  const [company, setCompany] = useState(job?.company ?? null);
  const [companyContacts, setCompanyContacts] = useState(job?.company?.contacts ?? []);

  // noinspection JSCheckFunctionSignatures
  const selectedCompanyID = watch(`${prefixWithSeparator(prefix)}company_id`, job?.company?.id ?? null);

  const selectedContactID = watch(`${prefixWithSeparator(prefix)}contact_id`, job?.contact?.id ?? null);
  const selectedEndUserID = watch(`${prefixWithSeparator(prefix)}end_user_id`, job?.end_user?.id ?? null);

  useEffect(() => {
    if (selectedCompanyID) {
      if (`${job?.company?.id}` === `${selectedCompanyID}`) {
        setCompany(job.company);
      } else {
        const retrieved = companies.current.find(({id}) => `${id}` === `${selectedCompanyID}`)
        if (retrieved) {
          setCompany(retrieved);
        }
      }
    } else {
      setCompany(null);
    }
  }, [selectedCompanyID, job]);

  useEffect(() => {
    setCompanyContacts((company?.contacts ?? []).map((contact) => ({
      value: contact.id,
      title: name(contact),
      isEndUser: contact.is_end_user,
    })));
  }, [company]);

  useEffect(() => {
    if (company) {
      if (selectedContactID) {
        const selectedContact = companyContacts.find(({value}) => `${value}` === `${selectedContactID}`);
        if (selectedContact) {
          setValue(`${prefixWithSeparator(prefix)}contact_name`, name(selectedContact));
        }
      }
      if (selectedEndUserID) {
        const selectedContact = companyContacts.find(({value}) => `${value}` === `${selectedEndUserID}`);
        if (selectedContact) {
          setValue(`${prefixWithSeparator(prefix)}end_user_name`, name(selectedContact));
        }
      }
    }
  }, [company, companyContacts, selectedContactID, selectedEndUserID, setValue, prefix]);

  const installers = useOptions('installers', useCallback((list) => list.map(({id, name}) => ({
    value: id,
    title: name
  })), []));

  return (
    <Panes
      entity={job}
      prefix={prefix}
      panes={[
        {
          title: 'Customer',
          fields: [
            <RelationAutocomplete
              valueName="company_id"
              titleName="company_name"
              prefix={prefix}
              label="Company"
              rules={{
                validate: () => {
                  const fieldPrefix = prefixWithSeparator(prefix);
                  if (!getValues(`${fieldPrefix}company_id`) && !getValues(`${fieldPrefix}contact_id`)) {
                    return 'Please select either a company or a contact';
                  }
                  return true;
                }
              }}
              loadOptions={query => (
                services.company.getCompanies({filter: {search: query}, include: 'contacts'}, 1, 50)
                  .then(response => {
                    companies.current = response.data;
                    return response.data.map(company => ({title: company.name, value: company.id}));
                  })
                  .catch(() => [])
              )}
              addForm={{
                title: 'Add New Company',
                onSaved: (company) => ({title: company.name, value: company.id}),
                render: (props) => (
                  <NewCompanyForm
                    {...props}
                    type="jobCompany"
                    company={{}}
                  />
                )
              }}
            />,
            ...(company ? [
              <Select
                name="contact_id"
                prefix={prefix}
                options={companyContacts.filter(({isEndUser}) => !isEndUser)}
                label="Contact"
              />,
              <Select
                name="end_user_id"
                prefix={prefix}
                options={companyContacts.filter(({isEndUser}) => !!isEndUser)}
                label="End User"
              />,
            ] : [
              <RelationAutocomplete
                valueName="contact_id"
                titleName="contact_name"
                prefix={prefix}
                label="Contact"
                loadOptions={query => (
                  services.contact.getContacts({filter: {search: query}}, 1, 50)
                    .then(response => response.data.map(contact => ({title: name(contact), value: contact.id})))
                    .catch(() => [])
                )}
                addForm={{
                  title: 'Add New Contact',
                  onSaved: (contact) => ({title: name(contact), value: contact.id}),
                  render: (props) => (
                    <NewContactForm
                      {...props}
                      type="jobContact"
                      contact={{}}
                    />
                  )
                }}
              />,
              <RelationAutocomplete
                valueName="end_user_id"
                titleName="end_user_name"
                prefix={prefix}
                label="End User"
                loadOptions={query => (
                  services.contact.getContacts({filter: {search: query}}, 1, 50)
                    .then(response => response.data.map(contact => ({title: name(contact), value: contact.id})))
                    .catch(() => [])
                )}
                addForm={{
                  title: 'Add New Contact',
                  onSaved: (contact) => ({title: name(contact), value: contact.id}),
                  render: (props) => (
                    <NewContactForm
                      {...props}
                      type="jobEndUser"
                      contact={{}}
                    />
                  )
                }}
              />
            ]),
            <Select
              name="installer_id"
              prefix={prefix}
              options={installers}
              label="Installer"
            />,
          ]
        },
        {
          title: 'Job Details', fields
        }
      ]}
      title={multiplePanes ? null : 'Job Details'}
    >{children}</Panes>
  );
}

export default withTemplatedFields(Fields);
